import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { TypeOfValue, VisualStyleEditorComponent } from '../base/VisualStyleEditor.component';
import { Visual } from '../Visual';
import { ChartVisual } from './ChartVisual.component';

@Component({
  selector: 'pbir-visual__chart-style-editor',
  templateUrl: './ChartVisualStyleEditor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChartStyleEditorComponent<
  TOptions extends ChartVisual.Options = ChartVisual.Options,
  TState extends ChartVisual.State = ChartVisual.State,
  TStyle extends ChartVisual.Style = ChartVisual.Style>
  extends VisualStyleEditorComponent<TOptions, TState, TStyle> {
  readonly LegendPositions = ChartVisual.LegendPosition.options;

  readonly NumberFormats = computed(() => {
    let visual = this.visual();
    if (!visual)
      return [];

    let options = [...ChartVisual.NumberFormat.options];

    let canShowPercentage = visual.percentageDisplayChart();
    if (!canShowPercentage)
      options = options.filter(x => x.id != ChartVisual.NumberFormat.Percentage);

    return options;
  });


  get noXAxis() { return !!this.visual().options().noXAxis; }
  get noYAxis() { return !!this.visual().options().noYAxis; }
  get noLegend() { return !!this.visual().options().noLegend; }
  get noDataLabels() { return !!this.visual().options().noDataLabels; }

  get axisLabels() { return this.style().axisLabels; }
  get axisLabelsModified() { return this.style().axisLabelsModified; }

  get xAxis() { return this.style().xAxis; }
  get xAxisModified() { return this.style().xAxisModified; }

  get yAxis() { return this.style().yAxis; }
  get yAxisModified() { return this.style().yAxisModified; }


  get legend() { return this.style().legend; }
  get legendModified() { return this.style().legendModified; }

  readonly legendPosition = computed(() => this.legend()?.position);
  setLegendPosition(value: ChartVisual.LegendPosition) {
    this.legendModified.set(true);
    this.updateValue(this.legend, x => ({ ...x, position: value }));
  }


  get dataLabels() { return this.style().dataLabels; }
  get dataLabelsModified() { return this.style().dataLabelsModified; }

  readonly dataLabelsBackgroundColor = computed(() => this.dataLabels()?.backgroundColor);
  readonly dataLabelsBorderColor = computed(() => this.dataLabels()?.borderColor);

  setDataLabelsBackgroundColor(value: string) {
    this.dataLabelsModified.set(true);
    this.updateValue(this.dataLabels, x => ({ ...x, backgroundColor: value }));
  }

  setDataLabelsBorderColor(value: string) {
    this.dataLabelsModified.set(true);
    this.updateValue(this.dataLabels, x => ({ ...x, borderColor: value }));
  }

  readonly dataLabelsNumberFormat = computed(() => {
    let format = this.dataLabels()?.numberFormat;

    if (!this.NumberFormats().some(x => x.id == format))
      format = ChartVisual.NumberFormat.Default;

    return format;
  });

  setDataLabelsNumberFormat(value: any) {
    this.dataLabelsModified.set(true);
    this.updateValue(this.dataLabels, x => ({ ...x, numberFormat: value }));
  }
}
